import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Activity, AlertTriangle, Cpu, MemoryStick, RefreshCw, Timer, Mail, MessageSquare, Inbox, Loader2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/recursos`;
// Umbrales sobre el % del límite real asignado al contenedor
const nivel = (pct) => (pct == null ? null : pct >= 80 ? 'rojo' : pct >= 60 ? 'amarillo' : 'verde');
const NIVEL_UI = {
  verde: { card: 'border-emerald-300', txt: 'text-emerald-700', bar: 'bg-emerald-500', label: 'Normal' },
  amarillo: { card: 'border-amber-400 bg-amber-50', txt: 'text-amber-700', bar: 'bg-amber-500', label: 'Alto, vigilar' },
  rojo: { card: 'border-red-400 bg-red-50', txt: 'text-red-600', bar: 'bg-red-500', label: 'Crítico' },
};
const REFRESH_MS = 30000;

const fmtUptime = (s) => {
  const d = Math.floor(s / 86400); const h = Math.floor((s % 86400) / 3600); const m = Math.floor((s % 3600) / 60);
  return `${d ? `${d}d ` : ''}${h}h ${m}m`;
};

export const semaforoCuenta = (c, ahora = Date.now()) => {
  if (c.ultimo_sync_ok === false || !c.ultimo_sync_at) return 'rojo';
  const min = (ahora - new Date(c.ultimo_sync_at).getTime()) / 60000;
  if (min > 180) return 'rojo';
  return min >= 90 ? 'naranja' : 'verde';
};

const COLOR = { verde: 'bg-emerald-500', naranja: 'bg-amber-500', rojo: 'bg-red-500' };
const LABEL = { verde: 'Operando', naranja: 'Demorado', rojo: 'Sin sincronizar' };

const Metrica = ({ icon: Icon, label, value, pct, detalle, nota, testid }) => {
  const n = nivel(pct); const ui = n ? NIVEL_UI[n] : null;
  return (
    <Card className={ui ? ui.card : ''} data-testid={testid}>
      <CardContent className="py-5">
        <div className="flex items-center justify-between gap-2 text-xs text-gray-500 mb-1">
          <span className="flex items-center gap-2"><Icon className="w-4 h-4" />{label}</span>
          {ui && <span className={`flex items-center gap-1 font-semibold ${ui.txt}`} data-testid={`${testid}-nivel-${n}`}>
            {n !== 'verde' && <AlertTriangle className="w-3 h-3" />}{ui.label}</span>}
        </div>
        <p className={`text-2xl font-bold ${ui && n !== 'verde' ? ui.txt : 'text-gray-900'}`}>{value}</p>
        {pct != null && (
          <div className="mt-2 h-1.5 rounded-full bg-gray-200 overflow-hidden">
            <div className={`h-full ${ui.bar}`} style={{ width: `${Math.min(100, pct)}%` }} />
          </div>
        )}
        {detalle && <p className="text-xs text-gray-600 mt-1">{detalle}</p>}
        {nota && <p className="text-[11px] text-gray-400 mt-1">{nota}</p>}
      </CardContent>
    </Card>
  );
};

const CuentaFila = ({ c }) => {
  const estado = semaforoCuenta(c);
  const min = c.ultimo_sync_at ? Math.round((Date.now() - new Date(c.ultimo_sync_at).getTime()) / 60000) : null;
  return (
    <div className="flex items-start gap-3 py-3 border-b last:border-b-0" data-testid={`recursos-cuenta-${c.email}`}>
      <span className={`w-3.5 h-3.5 rounded-full mt-1 shrink-0 ${COLOR[estado]}`} data-testid={`recursos-semaforo-${estado}`} title={LABEL[estado]} />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium truncate">{c.email}{c.label ? <span className="text-gray-400 font-normal"> · {c.label}</span> : null}</p>
        <p className="text-xs text-gray-500">
          {LABEL[estado]} · último sync: {c.ultimo_sync_at ? `${new Date(c.ultimo_sync_at).toLocaleString('es-AR')} (hace ${min} min)` : 'nunca'}
          {c.ultimo_sync_ok === false && ' · falló'}
        </p>
        {c.ultimo_sync_error && <p className="text-xs text-red-600 mt-0.5 break-words">{c.ultimo_sync_error}</p>}
      </div>
    </div>
  );
};

export default function AdminRecursosPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(false);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(API, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setData(r.data); setError(null);
    } catch (e) { setError(e.response?.data?.detail || 'No se pudieron obtener las métricas'); }
    setLoading(false);
  }, []);

  useEffect(() => { cargar(); const t = setInterval(cargar, REFRESH_MS); return () => clearInterval(t); }, [cargar]);

  const p = data?.proceso; const a = data?.app; const l = data?.limites || {};
  const pctMem = l.memoria_limite_mb && l.memoria_contenedor_mb != null ? (l.memoria_contenedor_mb / l.memoria_limite_mb) * 100 : null;
  const pctCpu = p && l.cpus ? p.cpu_percent / l.cpus : null;
  return (
    <div className="space-y-6" data-testid="admin-recursos-page">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Activity className="w-6 h-6" />Monitor de Recursos</h1>
          <p className="text-xs text-gray-500 mb-1 flex items-center gap-3 flex-wrap" data-testid="recursos-leyenda">
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-emerald-500" />menos de 60% del límite</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-amber-500" />60 a 80%</span>
            <span className="flex items-center gap-1"><span className="w-2.5 h-2.5 rounded-full bg-red-500" />más de 80%</span>
          </p>
          <p className="text-sm text-gray-500">Se actualiza cada 30 segundos{data && ` · última lectura ${new Date(data.generado_at).toLocaleTimeString('es-AR')}`}</p>
        </div>
        <Button variant="outline" size="sm" onClick={cargar} disabled={loading} data-testid="recursos-refresh">
          {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RefreshCw className="w-4 h-4 mr-1" />}Actualizar
        </Button>
      </div>
      {error && <p className="text-sm text-red-600" data-testid="recursos-error">{error}</p>}
      {!data ? <p className="text-sm text-gray-400">Cargando…</p> : (
        <>
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
            <Metrica icon={MemoryStick} label="RAM (RSS del backend)" value={`${p.rss_mb} MB`} testid="recursos-rss" pct={pctMem}
              detalle={pctMem != null ? `Servidor: ${l.memoria_contenedor_mb} MB de ${l.memoria_limite_mb} MB (${pctMem.toFixed(0)}%)` : null}
              nota={pctMem == null ? 'El servidor no informa su límite de memoria' : 'El color se mide contra el límite de memoria del plan'} />
            <Metrica icon={MemoryStick} label="Memoria virtual (VMS)" value={`${p.vms_mb} MB`} testid="recursos-vms"
              nota="Memoria reservada, no es consumo real" />
            <Metrica icon={Cpu} label="CPU" value={`${(pctCpu ?? p.cpu_percent).toFixed(0)}%`} testid="recursos-cpu" pct={pctCpu}
              detalle={l.cpus ? `de ${l.cpus} CPU asignada${l.cpus === 1 ? '' : 's'}` : null} />
            <Metrica icon={Activity} label="Threads" value={p.threads} testid="recursos-threads" />
            <Metrica icon={Timer} label="Uptime del proceso" value={fmtUptime(p.uptime_segundos)} testid="recursos-uptime" />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Metrica icon={RefreshCw} label="Sync de mail" value={a.mail_sync_en_curso ? 'En curso' : 'Inactivo'} testid="recursos-sync" />
            <Metrica icon={Inbox} label="Invitaciones pendientes (bandeja)" value={a.invitaciones_pendientes} testid="recursos-invitaciones" />
            <Metrica icon={MessageSquare} label="Sesiones WhatsApp activas (24 h)" value={a.sesiones_whatsapp_24h} testid="recursos-sesiones" />
          </div>
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2 flex-wrap">
                <Mail className="w-4 h-4" />Worker externo de mail (VPS)
                <Badge variant="outline" className="text-xs" data-testid="recursos-modo-sync">
                  {a.mail_sync_externo ? 'Sync interno pausado: lo hace el worker' : 'Sync interno activo'}
                </Badge>
              </CardTitle>
              <p className="text-xs text-gray-500">Verde: OK hace menos de 90 min · Naranja: 90 a 180 min · Rojo: más de 180 min o último sync fallido</p>
            </CardHeader>
            <CardContent>
              {data.cuentas_mail.length === 0
                ? <p className="text-sm text-gray-400" data-testid="recursos-sin-cuentas">No hay cuentas de mail configuradas.</p>
                : data.cuentas_mail.map(c => <CuentaFila key={c.email} c={c} />)}
            </CardContent>
          </Card>
        </>
      )}
    </div>
  );
}
